import React, { ReactNode } from 'react'
import { Trans, t } from '@lingui/macro'
import { Box, Flex, HStack, MenuBulkAction } from 'front-end/src/components'
import ResettableInput from '../../../../../components/common/form/input/ResettableInput'
import { InviteeActionsMenuList } from './InviteeActionsMenuList'
import Select, {
  SelectVariant
} from '../../../../../components/common/select/Select'
import {
  getStatus,
  StatusOfEventProps
} from 'front-end/src/pages/manage-events/components/event-status/EventStatus'
import { useManageEventContext } from '../../../ManageEventContext'
import { InviteeItemFragment } from './AttendanceTab.codegen'
import { AttendanceFilter } from 'front-end/src/pages/manage-events/event-edit/types/AttendanceFilter'
import { attendanceStatusOptions } from 'front-end/src/pages/manage-events/event-edit/tabs/attendance/attendanceStatusOptions'

type InviteeListHeaderProps = {
  search: string
  setSearchValue: React.Dispatch<React.SetStateAction<string>>
  selectedStatus: AttendanceFilter
  changeFilters
  onFilterChange
  onSelectAllChange: (selected: boolean) => void
  selectedInvitees: InviteeItemFragment[]
  children: ReactNode
}

const InviteeListHeader = ({
  search,
  setSearchValue,
  selectedStatus,
  changeFilters,
  onFilterChange: filterChangeCallback,
  selectedInvitees,
  children
}: InviteeListHeaderProps) => {
  const { event, isRecurringSeries, isRecurringInstance } =
    useManageEventContext()

  const onSearchReset = () => {
    setSearchValue('')
    changeFilters({ search: '' })
  }

  const { id: eventId } = event

  const isAttendanceAllow =
    getStatus(event as StatusOfEventProps) === 'in-progress' ||
    getStatus(event as StatusOfEventProps) === 'completed'

  const certificationInvitees = selectedInvitees.filter(
    (invitee) => invitee.certification_id !== null
  ).length
  const groupInvitees = selectedInvitees.filter(
    (invitee) => invitee.group_id !== null
  ).length

  const bulkActionDisabled = selectedInvitees.length < 1

  const onFilterChange = () => {
    filterChangeCallback()
  }

  return (
    <>
      <HStack spacing="8px">
        <Flex
          align="center"
          pointerEvents={bulkActionDisabled ? 'none' : 'auto'}
        >
          {/* Dropdown cannot be used because causes problem on remove invitee modal (after click on toggle)*/}
          <MenuBulkAction isDisabled={bulkActionDisabled} dataTest="MenuList">
            <InviteeActionsMenuList
              isRecurringSeries={isRecurringSeries}
              isRecurringInstance={isRecurringInstance}
              allowAttendance={isAttendanceAllow}
              allowRemove={!(certificationInvitees + groupInvitees)}
              reasonNotAllowRemove={
                certificationInvitees
                  ? t`Certification`
                  : groupInvitees
                  ? t`Group`
                  : null
              }
              selectProps={{
                refetchInvitees: onFilterChange,
                eventId: eventId,
                selectedInvitees
              }}
            />
          </MenuBulkAction>
        </Flex>

        <Box as="label" className="FilterSelect">
          <Select
            label={<Trans>RSVP</Trans>}
            variant={SelectVariant.GREY}
            minWidth={170}
            maxWidth={170}
            options={attendanceStatusOptions}
            onChange={(selectedStatus) => {
              if (selectedStatus) {
                changeFilters({
                  selectedStatus,
                  page: 1,
                  selectedInvitees: [],
                  allSelected: false
                })
              }
            }}
            value={selectedStatus}
          />
        </Box>

        <Box maxW="180px">
          <ResettableInput
            debouncedOnChange={(value) => {
              changeFilters({
                search: value,
                page: 1,
                selectedInvitees: [],
                allSelected: false
              })
            }}
            debounceTime={1000}
            onChange={(value) => {
              setSearchValue(value)
            }}
            autoFocus
            onReset={onSearchReset}
            title={t`Search`}
            placeholder={t`Search`}
            showResetButton={!!search}
            showSearchIcon
            value={search}
          />
        </Box>
      </HStack>

      {children}
    </>
  )
}

export { InviteeListHeader }
